import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus } from 'lucide-react'
import { AddUser } from './AddUser'
import Loader from '../../../../common/components/Loader'
import { User } from '../../../../common/models'
import { IColumn, Table } from '../../../../core/components/Table'
import { formatDateTime } from '../../../../core/constants/times'
import { useTableParams } from '../../../../core/hooks/table-params'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import { getUsers } from '../../../../core/services/users'
import { useAppSelector } from '../../../../store'
import { MessageBox } from '../../messages/MessageBox'
import { Pagination } from '../../Pagination'
import { Icon } from '@/common/components/Icon'

export const SettingPermissionsUsersPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets,
    searchParams,
  } = useTableParams()

  const { hasEditPermission } = useCheckPermission({
    page: 'PERMISSIONS_EDIT_USER',
  })

  const [items, setItems] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [addEditModal, setAddEditModal] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const { festivalName, editionId } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getUsers({
        page,
        size,
        sortBy,
      })
      setItems(response.data.content)
      setTotalElemenets(response.data.totalElements)
      setSize(response.data.size)
      setPage(response.data.pageNumber + 1)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    sortBy,
    setPage,
    setSize,
    setTotalElemenets,
    editionId,
    festivalName,
  ])

  useEffect(() => {
    getData()
  }, [getData])

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(items.map((one) => one.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      setLoading(true)
      try {
        // const params: InvitesPaginationParams = {
        //   page,
        //   size,
        //   startDate,
        //   endDate,
        // }
        // if (searchParams.current.redeemCode) {
        //   params.redeemCode = searchParams.current.redeemCode
        // }
        // const { data, status } = await getUsers(params)
        // if (status === 200) {
        //   setItems(data.content)
        //   setTotalElemenets(data.totalElements)
        // }
      } catch (err) {
        console.log(err)
      } finally {
        setLoading(false)
      }
    },
    [page, size, sortBy],
  )

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: false,
      searchable: false,
      render: (row) => `${row.firstName} ${row.lastName}`,
    },
    {
      title: 'Email',
      accessor: 'email',
      sortable: false,
      searchable: false,
    },
    {
      title: 'User Group',
      accessor: 'userGroup',
      sortable: false,
      searchable: false,
    },
    {
      title: 'Last Activity',
      accessor: 'userGroup',
      sortable: false,
      searchable: false,
      render: (row) => formatDateTime(row.lastActivityDate),
    },
    {
      title: 'Active',
      accessor: 'active',
      sortable: false,
      render: (row) => <Checkbox checked={row.active} disabled />,
    },
    {
      title: 'Custom Permissions',
      accessor: 'customPermissions',
      sortable: false,
      render: (row) => <Checkbox checked={row.customPermissions} disabled />,
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <Link
          className="view text-accent-1 underline"
          to={`/settings/permissions/users/${festivalName}/${editionId}/view/${row.id}`}
        >
          View
        </Link>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Users"
        actions={
          <>
            {hasEditPermission && (
              <Button
                onClick={() => {
                  setAddEditModal(true)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title={'Users'} className="mb-5" icon="users-filled" />
        <Table
          handleSearch={handleSearchParams}
          searchParams={searchParams.current}
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={items}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <AddUser
        visible={addEditModal}
        setVisible={() => {
          setAddEditModal(false)
        }}
        onSuccess={getData}
        disabled={!hasEditPermission}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
